import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { firestoreService } from '@/utils/firestoreService'

export interface EmailSettings {
  defaultSignature: string
  defaultTemplate: string | null
  defaultFrom: string
  autoBcc: boolean
  trackOpens: boolean
  replyTo: string
}

export type NotificationFrequency = 'immediate' | 'daily' | 'weekly'

export interface NotificationSettings {
  emailNotifications: boolean
  pushNotifications: boolean
  smsNotifications: boolean
  frequency: NotificationFrequency
  emailDefaultTo?: string | null
  events: {
    transactionFinalized: {
      email: boolean
      push: boolean
      sms: boolean
      emailTo?: string | null
    }
  }
}

interface CrmSettings {
  defaultPriority: string | null
  reminderTime: string | null
  leadScoreThreshold: number
  autoAssignTasks: boolean
  trackActivities: boolean
  activityTypes: string[]
}

interface CommoditySettings {
  categories: string[]
}

export interface VehicleFieldConfig {
  key: string
  label: string
  enabled: boolean
}

export type RuleOperator = 'AND' | 'OR' | 'IF_THEN'
export type RuleCondition = 'HAS_VALUE' | 'IS_EMPTY' | 'EQUALS' | 'CONTAINS'

export interface VehicleValidationRule {
  id: string
  name: string
  description: string
  type: 'required' | 'conditional' | 'group' | 'business'
  operator?: RuleOperator
  conditions?: RuleCondition[]
  fields: string[]
  message: string
  enabled: boolean
}

export interface VehicleRuleGroup {
  id: string
  name: string
  description: string
  operator: 'AND' | 'OR'
  rules: string[] // Rule IDs
  enabled: boolean
}

interface TransactionSettings {
  defaultCurrency: string | null
  numberFormat: string
  taxRate: number
  defaultTemplate: string | null
  vehicleFieldConfig: VehicleFieldConfig[]
  vehicleValidationRules: VehicleValidationRule[]
  vehicleRuleGroups: VehicleRuleGroup[]
  autoNumber: boolean
  requireApproval: boolean
  approvalThreshold: number
  requirePhoneNumber: boolean
}

interface Settings {
  email: EmailSettings
  crm: CrmSettings
  commodity: CommoditySettings
  transactions: TransactionSettings
  notifications: NotificationSettings
  idDocuments: IDDocumentSettings
  phone: PhoneSettings
}

export interface IDDocumentSettings {
  requireBackPhoto: boolean
}

export interface PhoneSettings {
  callUrlType: 'default' | 'custom'
  callUrlTemplate: string
  messageUrlTemplate: string
}

const defaultSettings: Settings = {
  email: {
    defaultSignature: '',
    defaultTemplate: null,
    defaultFrom: 'Scrap Brothers LLC <info@scrapbros.net>',
    autoBcc: false,
    trackOpens: true,
    replyTo: '',
  },
  crm: {
    defaultPriority: null,
    reminderTime: null,
    leadScoreThreshold: 50,
    autoAssignTasks: true,
    trackActivities: true,
    activityTypes: [],
  },
  commodity: {
    categories: ['Ferrous', 'Non-ferrous', 'Electronic', 'Precious Metal', 'Other'],
  },
  transactions: {
    defaultCurrency: null,
    numberFormat: 'TRX-{year}-{number}',
    taxRate: 0,
    defaultTemplate: null,
    vehicleFieldConfig: [],
    vehicleValidationRules: [],
    vehicleRuleGroups: [],
    autoNumber: true,
    requireApproval: false,
    approvalThreshold: 1000,
    requirePhoneNumber: true,
  },
  notifications: {
    emailNotifications: true,
    pushNotifications: true,
    smsNotifications: false,
    frequency: 'immediate',
    emailDefaultTo: null,
    events: {
      transactionFinalized: { email: true, push: false, sms: false, emailTo: null },
    },
  },
  idDocuments: {
    requireBackPhoto: false,
  },
  phone: {
    callUrlType: 'default',
    callUrlTemplate: 'openphone://dial?number={{phone}}',
    messageUrlTemplate: 'sms:{{phone}}',
  },
}

export const useSettingsStore = defineStore(
  'settings',
  () => {
    const settings = ref<Settings>(defaultSettings)
    const isLoading = ref(false)
    const error = ref<string | null>(null)

    // Getters
    const emailSettings = computed(() => settings.value.email)
    const crmSettings = computed(() => settings.value.crm)
    const commoditySettings = computed(() => settings.value.commodity)
    const transactionSettings = computed(() => settings.value.transactions)
    const notificationSettings = computed(() => settings.value.notifications)
    const idDocumentSettings = computed(() => settings.value.idDocuments)
    const phoneSettings = computed(() => settings.value.phone)

    // Actions
    async function loadSettings() {
      isLoading.value = true
      error.value = null

      try {
        // For now, we'll use a single settings document
        const globalSettings = await firestoreService.get<Partial<Settings>>('settings', 'global')
        if (globalSettings) {
          // Merge with defaults to ensure newly added keys exist
          const merged: Settings = {
            ...defaultSettings,
            ...(globalSettings as Settings),
            email: { ...defaultSettings.email, ...(globalSettings.email || {}) },
            crm: { ...defaultSettings.crm, ...(globalSettings.crm || {}) },
            commodity: { ...defaultSettings.commodity, ...(globalSettings.commodity || {}) },
            transactions: {
              ...defaultSettings.transactions,
              ...(globalSettings.transactions || {}),
            },
            notifications: {
              ...defaultSettings.notifications,
              ...(globalSettings.notifications || {}),
            },
            idDocuments: {
              ...defaultSettings.idDocuments,
              ...(globalSettings.idDocuments || {}),
            },
            phone: {
              ...defaultSettings.phone,
              ...(globalSettings.phone || {}),
            },
          }

          // Backward-compat: migrate boolean event flags to channel object shape
          const evt: any = (merged.notifications as any).events
          if (evt && typeof evt.transactionFinalized === 'boolean') {
            evt.transactionFinalized = {
              email: Boolean(evt.transactionFinalized),
              push: false,
              sms: false,
            }
          }

          settings.value = merged
        } else {
          // Initialize settings if they don't exist
          await firestoreService.set('settings', {
            id: 'global',
            ...defaultSettings,
          })
        }
      } catch (err) {
        error.value = 'Failed to load settings'
        console.error('Failed to load settings:', err)
      } finally {
        isLoading.value = false
      }
    }

    async function saveSettings() {
      try {
        await firestoreService.update('settings', 'global', settings.value)
      } catch (err) {
        error.value = 'Failed to save settings'
        console.error('Failed to save settings:', err)
        throw err
      }
    }

    async function updateEmailSettings(newSettings: Partial<EmailSettings>) {
      settings.value.email = { ...settings.value.email, ...newSettings }
      await saveSettings()
    }

    async function updateCrmSettings(newSettings: Partial<CrmSettings>) {
      settings.value.crm = { ...settings.value.crm, ...newSettings }
      await saveSettings()
    }

    async function updateCommoditySettings(newSettings: Partial<CommoditySettings>) {
      settings.value.commodity = { ...settings.value.commodity, ...newSettings }
      await saveSettings()
    }

    async function updateTransactionSettings(newSettings: Partial<TransactionSettings>) {
      settings.value.transactions = { ...settings.value.transactions, ...newSettings }
      await saveSettings()
    }

    async function updateNotificationSettings(newSettings: Partial<NotificationSettings>) {
      settings.value.notifications = { ...settings.value.notifications, ...newSettings }
      await saveSettings()
    }

    async function updateIDDocumentSettings(newSettings: Partial<IDDocumentSettings>) {
      settings.value.idDocuments = { ...settings.value.idDocuments, ...newSettings }
      await saveSettings()
    }

    async function updatePhoneSettings(newSettings: Partial<PhoneSettings>) {
      settings.value.phone = { ...settings.value.phone, ...newSettings }
      await saveSettings()
    }

    return {
      settings,
      emailSettings,
      crmSettings,
      commoditySettings,
      transactionSettings,
      notificationSettings,
      idDocumentSettings,
      phoneSettings,
      isLoading,
      error,
      loadSettings,
      saveSettings,
      updateEmailSettings,
      updateCrmSettings,
      updateCommoditySettings,
      updateTransactionSettings,
      updateNotificationSettings,
      updateIDDocumentSettings,
      updatePhoneSettings,
    }
  },
  {
    persist: {
      storage: localStorage,
      paths: ['settings'],
    } as any,
  },
)
